import { DateTime } from "luxon";
import { trpc } from "../../main/components/ProviderContainer.tsx";
import React from "react";
import FinalizeDraftPanel from "../components/FinalizeDraftPanel.tsx";
import ConfirmModal from "../components/ConfirmModal.tsx";
import { ArrowReturnLeft } from "react-bootstrap-icons";
import LockedBadge from "../../utils/components/LockedBadge.tsx";
import ToPayInvoiceUpload from "../components/ToPayInvoiceUpload.tsx";
import { Link, useLocation, useSearch } from "wouter";
import ResolveDraftAsAccountedModal from "../components/ResolveDraftAsAccountedModal.tsx";
import { formatMatchedOn } from "../components/DuplicateFactureWarningModal.tsx";

const formatDate = (date: Date | null | undefined) => {
  if (!date) {
    return "-";
  }

  return DateTime.fromJSDate(date).toFormat("dd/MM/yyyy");
};

const formatAmount = (amountCent: number) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(Math.abs(amountCent) / 100);
};

const FacturesAPayerContainer = () => {
  const utils = trpc.useUtils();
  const { data, isLoading, error } = trpc.facture.getToPayOverview.useQuery();
  const [, setLocation] = useLocation();
  const queryString = useSearch();
  const selectedDraftId = new URLSearchParams(queryString).get("draftId");
  const setSelectedDraftId = (id: string | null) => setLocation(
    `~/factures/facturesAPayer${id ? `?draftId=${encodeURIComponent(id)}` : ""}`,
  );
  const [resolution, setResolution] = React.useState<{ draftId: string; factureId: string } | null>(null);
  const [returningDraftId, setReturningDraftId] = React.useState<string | null>(
    null,
  );
  const [notice, setNotice] = React.useState<string | null>(null);
  const returnMutation = trpc.facture.returnToReview.useMutation({
    onSuccess: async (_, variables) => {
      const returned = data?.draftsToPay.find((draft) => draft._id === variables.draftId);
      await Promise.all([
        utils.facture.invalidate(),
        utils.superPdp.invalidate(),
      ]);
      if (selectedDraftId === variables.draftId) setSelectedDraftId(null);
      setReturningDraftId(null);
      setNotice(`La facture est à nouveau disponible dans « ${returned?.source === "superpdp" ? "Factures électroniques" : "À traiter"} ». Son PDF et ses informations sont conservés.`);
    },
  });

  if (isLoading) {
    return <div className="p-3">Chargement des factures à payer...</div>;
  }
  if (error) return <div className="alert alert-danger" role="alert">{error.message}</div>;

  const draftsToPay = data?.draftsToPay || [];

  const selectedDraft =
    draftsToPay.find((draft) => draft._id === selectedDraftId) || null;
  const returningDraft =
    draftsToPay.find((draft) => draft._id === returningDraftId) || null;
  const resolvingDraft = draftsToPay.find((draft) => draft._id === resolution?.draftId);
  const resolvingCandidate = resolvingDraft?.duplicates.find((candidate) =>
    candidate.kind === "facture" && candidate._id === resolution?.factureId,
  );

  const openDraftPdf = async (docId: string, fallbackUrl: string) => {
    let url = fallbackUrl;
    try {
      const freshUrl = await utils.facture.refreshImportedDocPdfUrl.fetch({
        docId,
      });
      url = freshUrl;
    } catch {
      // keep the fallback url when the refresh fails
    }
    window.open(url, "_blank", "noreferrer");
  };

  return (
    <div className="container-fluid px-0 d-flex flex-column gap-4">
      <ToPayInvoiceUpload />
      {notice ? <div className="alert alert-success" role="status">{notice}</div> : null}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="fw-bold">Factures à régler</div>
          <span className="badge text-bg-info">{draftsToPay.length}</span>
        </div>
        <div className="card-body">
          {returnMutation.error ? (
            <div className="alert alert-danger" role="alert">
              {returnMutation.error.message}
            </div>
          ) : null}
          {draftsToPay.length === 0 ? (
            <div className="text-muted">
              Aucune facture en attente de règlement.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Libellé</th>
                    <th>Référence</th>
                    <th>Date</th>
                    <th>Échéance</th>
                    <th>Montant</th>
                    <th>Document</th>
                    <th>Source</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {draftsToPay.map((draft) => (
                    <tr key={draft._id}>
                       <td>
                         {draft.libellé}
                         {draft.duplicates.length ? <div className="small text-warning-emphasis mt-2">
                           <strong>Doublon possible</strong>
                           {draft.duplicates.map((candidate) => <div key={`${candidate.kind}:${candidate._id}`} className="mt-2">
                             <div>{candidate.kind === "facture" ? "Facture comptabilisée" : "Facture en attente de règlement"} : {candidate.libellé}</div>
                             <div>Réf. {candidate.modeRef || "—"} · {formatDate(candidate.date)} · {formatAmount(candidate.montantCent)}</div>
                             <div>{formatMatchedOn(candidate.matchedOn)}</div>
                             <Link className="btn btn-outline-secondary btn-sm mt-1"
                               href={candidate.kind === "facture"
                                 ? `~/factures/factureInput?factureId=${encodeURIComponent(candidate._id)}`
                                 : `~/factures/facturesAPayer?draftId=${encodeURIComponent(candidate._id)}`}>
                               {candidate.kind === "facture" ? "Voir la facture" : "Voir la facture à régler"}
                             </Link>
                             {candidate.kind === "facture" ? <button type="button"
                               className="btn btn-outline-warning btn-sm ms-1 mt-1"
                               disabled={draft.locked === true || returnMutation.isPending}
                               onClick={() => setResolution({ draftId: draft._id, factureId: candidate._id })}>
                               Déjà comptabilisée…
                             </button> : null}
                           </div>)}
                         </div> : null}
                       </td>
                      <td>{draft.modeRef}</td>
                      <td>
                        {formatDate(draft.dateFacture || draft.datePrelevement)}
                      </td>
                      <td>{formatDate(draft.paymentDueDate)}</td>
                      <td>{formatAmount(draft.montantCent)}</td>
                      <td>{draft.originalFileName}</td>
                      <td>
                        <span className="badge text-bg-light">
                          {draft.source}
                        </span>
                        {draft.locked === true ? <LockedBadge /> : null}
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2">
                          <a
                            className="btn btn-outline-secondary btn-sm"
                            href={draft.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => {
                              event.preventDefault();
                               void openDraftPdf(draft.docId, draft.pdfUrl);
                            }}
                          >
                            Voir PDF
                          </a>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={
                               returnMutation.isPending || draft.locked === true
                            }
                            onClick={() => {
                              if (draft.locked !== true) {
                                setSelectedDraftId(draft._id);
                              }
                            }}
                          >
                            Régler
                          </button>
                          <button
                            type="button"
                             className="btn btn-outline-secondary btn-sm"
                             title={selectedDraftId === draft._id ? "Utilisez le bouton du formulaire pour conserver vos modifications" : "Remettre à traiter"}
                             aria-label="Remettre à traiter"
                            disabled={
                               returnMutation.isPending || draft.locked === true || selectedDraftId === draft._id
                            }
                            onClick={() => {
                              if (draft.locked !== true) {
                                 setReturningDraftId(draft._id);
                              }
                            }}
                          >
                             <ArrowReturnLeft size={16} className="me-1" />
                             Remettre à traiter
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {data?.resolvedDrafts.length ? <details className="card p-3">
        <summary className="fw-bold">Déjà comptabilisées ({data.resolvedDrafts.length})</summary>
        <div className="table-responsive mt-3"><table className="table align-middle">
          <thead><tr><th>Libellé / référence</th><th>Date</th><th>Montant</th><th>Résolution</th><th>Documents</th></tr></thead>
          <tbody>{data.resolvedDrafts.map((draft) => <tr key={draft._id}>
            <td>{draft.libellé}<div className="small text-muted">{draft.modeRef}</div></td>
            <td>{formatDate(draft.dateFacture || draft.datePrelevement)}</td>
            <td>{formatAmount(draft.montantCent)}</td><td>{formatDate(draft.resolvedAt)}</td>
            <td><a href={draft.pdfUrl} target="_blank" rel="noreferrer"
              onClick={(event) => { event.preventDefault(); void openDraftPdf(draft.docId, draft.pdfUrl); }}>PDF conservé</a>
              {draft.resolvedFactureId ? <Link className="ms-3"
                href={`~/factures/factureInput?factureId=${encodeURIComponent(draft.resolvedFactureId)}`}>Facture comptabilisée</Link> : null}</td>
          </tr>)}</tbody>
        </table></div>
      </details> : null}

      {resolvingDraft && resolvingCandidate ? <ResolveDraftAsAccountedModal
        key={`${resolvingDraft._id}:${resolvingCandidate._id}`} draft={resolvingDraft}
        candidate={resolvingCandidate} onClose={() => setResolution(null)} /> : null}

      {selectedDraft ? (
        <FinalizeDraftPanel
          draft={selectedDraft}
          onClose={() => setSelectedDraftId(null)}
          onCreated={() => setSelectedDraftId(null)}
        />
      ) : null}
      <ConfirmModal
        show={returningDraft != null}
        title="Remettre cette facture à traiter ?"
        onCancel={() => { if (!returnMutation.isPending) setReturningDraftId(null); }}
        onConfirm={() => {
          if (!returningDraftId) {
            return;
          }
          if (returningDraft?.locked === true) {
            setReturningDraftId(null);
            return;
          }
          returnMutation.mutate({ draftId: returningDraftId });
        }}
        confirmLabel="Remettre à traiter"
        submittingLabel="Remise à traiter..."
        isSubmitting={returnMutation.isPending}
      >
        <p className="mb-0">
          Cette facture sera retirée des règlements en attente. Vous pourrez reprendre son traitement
          dans « {returningDraft?.source === "superpdp" ? "Factures électroniques" : "À traiter"} ».
          Son PDF et les informations déjà renseignées seront conservés.
        </p>
      </ConfirmModal>
    </div>
  );
};

export default FacturesAPayerContainer;
